import { useState, useMemo, useEffect } from 'react'
import { DollarSign, Settings, Plus, Edit2, Trash2, ChevronDown, ChevronUp, Info } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { supabase } from '../lib/supabase'
import { Button, Card, StatCard, Input, Select, Modal, EmptyState, PageHeader, Badge, Toast } from '../components/ui/SharedUI'

/**
 * Rate structure stored in rates.by_client (jsonb):
 * {
 *   "recipient_id": {
 *     "base_rate": 12.00,
 *     "differentials": [
 *       { "name": "Total care", "amount": 4.00 },
 *       { "name": "Weekend", "amount": 2.00 }
 *     ],
 *     "total_rate": 16.00,
 *     "agency_allocation": 33.00,
 *     "notes": "ICWP total care rate via Serenity"
 *   }
 * }
 *
 * Fallback: rates.single_rate used for shifts without a recipient-specific rate.
 */

const COMMON_DIFFERENTIALS = [
  'Total care',
  'Skilled care',
  'Weekend',
  'Holiday',
  'Overnight',
  'Live-in',
  'Training',
  'Mileage',
]

export function PayPage() {
  const { user } = useAuth()
  const { data: shifts } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: false })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id', orderBy: 'name', ascending: true })
  const [rates, setRates] = useState(null)
  const [loading, setLoading] = useState(true)
  const [rateModalOpen, setRateModalOpen] = useState(false)
  const [editingRecipient, setEditingRecipient] = useState(null)
  const [defaultRateOpen, setDefaultRateOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const [expandedRows, setExpandedRows] = useState({})

  // Rate form for per-recipient configuration
  const emptyRateForm = {
    base_rate: '',
    differentials: [],
    agency_allocation: '',
    notes: '',
  }
  const [rateForm, setRateForm] = useState(emptyRateForm)
  const [defaultRate, setDefaultRate] = useState('')
  const [newDiffName, setNewDiffName] = useState('')
  const [newDiffAmount, setNewDiffAmount] = useState('')

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r]))

  useEffect(() => {
    if (!user) return
    async function load() {
      const { data } = await supabase.from('rates').select('*').eq('caregiver_id', user.id).single()
      if (data) {
        setRates(data)
        setDefaultRate(data.single_rate || '')
      }
      setLoading(false)
    }
    load()
  }, [user])

  function getRate(shift) {
    if (!rates) return { total: 0, base: 0, differentials: [], allocation: null }
    const clientRates = rates.by_client || {}
    const recipientRate = shift.recipient_id ? clientRates[shift.recipient_id] : null

    if (recipientRate) {
      return {
        total: recipientRate.total_rate || 0,
        base: recipientRate.base_rate || 0,
        differentials: recipientRate.differentials || [],
        allocation: recipientRate.agency_allocation || null,
        notes: recipientRate.notes || '',
      }
    }

    // Fall back to default rate
    return {
      total: rates.single_rate || 0,
      base: rates.single_rate || 0,
      differentials: [],
      allocation: null,
      notes: '',
    }
  }

  // ── Stats ──────────────────────────────────────────────
  const stats = useMemo(() => {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay())
    startOfWeek.setHours(0, 0, 0, 0)

    // Exclude observed shifts from earnings — they were worked by someone else
    const completed = shifts.filter(s => s.clock_out && !s.is_active && s.authorship !== 'observed')

    const calc = (arr) => arr.reduce((sum, s) => {
      const hours = (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000
      return sum + hours * getRate(s).total
    }, 0)

    const calcHours = (arr) => arr.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)

    const month = completed.filter(s => new Date(s.clock_in) >= startOfMonth)
    const week = completed.filter(s => new Date(s.clock_in) >= startOfWeek)

    const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const prevMonth = completed.filter(s => {
      const d = new Date(s.clock_in)
      return d >= prevMonthStart && d < startOfMonth
    })

    // Calculate what agency billed vs what aide got paid (if allocation data exists)
    const monthAllocation = month.reduce((sum, s) => {
      const hours = (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000
      const r = getRate(s)
      return sum + (r.allocation ? hours * r.allocation : 0)
    }, 0)

    return {
      weekEarnings: calc(week),
      monthEarnings: calc(month),
      prevMonthEarnings: calc(prevMonth),
      monthHours: calcHours(month),
      monthAllocation,
      hasAllocationData: monthAllocation > 0,
    }
  }, [shifts, rates])

  const delta = stats.prevMonthEarnings > 0
    ? ((stats.monthEarnings - stats.prevMonthEarnings) / stats.prevMonthEarnings * 100).toFixed(0)
    : null

  // ── Rate CRUD ──────────────────────────────────────────

  function openRecipientRate(recipient) {
    setEditingRecipient(recipient)
    const existing = rates?.by_client?.[recipient.id]
    if (existing) {
      setRateForm({
        base_rate: existing.base_rate || '',
        differentials: existing.differentials || [],
        agency_allocation: existing.agency_allocation || '',
        notes: existing.notes || '',
      })
    } else {
      setRateForm(emptyRateForm)
    }
    setRateModalOpen(true)
  }

  function addDifferential() {
    if (!newDiffName.trim() || !newDiffAmount) return
    setRateForm({
      ...rateForm,
      differentials: [...rateForm.differentials, { name: newDiffName.trim(), amount: parseFloat(newDiffAmount) || 0 }],
    })
    setNewDiffName('')
    setNewDiffAmount('')
  }

  function removeDifferential(index) {
    setRateForm({
      ...rateForm,
      differentials: rateForm.differentials.filter((_, i) => i !== index),
    })
  }

  function calcTotal() {
    const base = parseFloat(rateForm.base_rate) || 0
    const diffs = rateForm.differentials.reduce((sum, d) => sum + (d.amount || 0), 0)
    return base + diffs
  }

  async function handleSaveRecipientRate(e) {
    e.preventDefault()
    if (!editingRecipient) return

    try {
      const total = calcTotal()
      const clientRates = { ...(rates?.by_client || {}) }
      clientRates[editingRecipient.id] = {
        base_rate: parseFloat(rateForm.base_rate) || 0,
        differentials: rateForm.differentials,
        total_rate: total,
        agency_allocation: parseFloat(rateForm.agency_allocation) || null,
        notes: rateForm.notes || '',
      }

      const payload = {
        caregiver_id: user.id,
        mode: 'by_client',
        single_rate: parseFloat(defaultRate) || null,
        by_client: clientRates,
      }

      if (rates) {
        await supabase.from('rates').update(payload).eq('id', rates.id)
      } else {
        await supabase.from('rates').insert(payload)
      }

      const { data } = await supabase.from('rates').select('*').eq('caregiver_id', user.id).single()
      setRates(data)
      setRateModalOpen(false)
      setToast({ message: `Rate saved for ${editingRecipient.name}: $${total.toFixed(2)}/hr`, type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  async function handleSaveDefaultRate(e) {
    e.preventDefault()
    try {
      const payload = {
        caregiver_id: user.id,
        mode: rates?.mode || 'by_client',
        single_rate: parseFloat(defaultRate) || null,
        by_client: rates?.by_client || {},
      }

      if (rates) {
        await supabase.from('rates').update(payload).eq('id', rates.id)
      } else {
        await supabase.from('rates').insert(payload)
      }

      const { data } = await supabase.from('rates').select('*').eq('caregiver_id', user.id).single()
      setRates(data)
      setDefaultRateOpen(false)
      setToast({ message: 'Default rate saved', type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  async function removeRecipientRate(recipientId) {
    const clientRates = { ...(rates?.by_client || {}) }
    delete clientRates[recipientId]
    await supabase.from('rates').update({ by_client: clientRates }).eq('id', rates.id)
    const { data } = await supabase.from('rates').select('*').eq('caregiver_id', user.id).single()
    setRates(data)
    setToast({ message: 'Rate removed', type: 'success' })
  }

  function toggleRow(id) {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }))
  }

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  const clientRates = rates?.by_client || {}
  const hasRates = rates && (rates.single_rate || Object.keys(clientRates).length > 0)

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }}>
      <PageHeader
        title="Pay"
        description="Configure rates per client and track documented earnings."
      />

      {/* ── Rate cards per recipient ────────────────────── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 500, color: '#1A202C' }}>Pay rates by client</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" size="sm" onClick={() => setDefaultRateOpen(true)}>Default rate</Button>
          </div>
        </div>

        {recipients.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '2rem', color: '#718096' }}>
            <p style={{ marginBottom: '8px' }}>Add a care recipient first, then configure their pay rate.</p>
          </Card>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {recipients.map(r => {
              const rate = clientRates[r.id]
              return (
                <Card key={r.id} style={{ padding: '1rem 1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C' }}>{r.name}</span>
                        {r.program && <Badge>{r.program.toUpperCase()}</Badge>}
                        {r.agency_name && <span style={{ fontSize: '13px', color: '#718096' }}>via {r.agency_name}</span>}
                      </div>
                      {rate ? (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '22px', fontWeight: 600, color: '#2C7A7B' }}>${rate.total_rate?.toFixed(2)}/hr</span>
                          {rate.differentials?.length > 0 && (
                            <span style={{ fontSize: '13px', color: '#718096' }}>
                              (${rate.base_rate?.toFixed(2)} base
                              {rate.differentials.map(d => ` + $${d.amount.toFixed(2)} ${d.name.toLowerCase()}`).join('')})
                            </span>
                          )}
                          {rate.agency_allocation && (
                            <span style={{ fontSize: '12px', color: '#8b5000', background: '#FCF5E8', padding: '2px 8px', borderRadius: '4px' }}>
                              Agency bills ${rate.agency_allocation.toFixed(2)}/hr
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: '14px', color: '#A0AEC0' }}>
                          {rates?.single_rate ? `Using default rate: $${rates.single_rate}/hr` : 'No rate configured'}
                        </span>
                      )}
                      {rate?.notes && (
                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px', fontStyle: 'italic' }}>{rate.notes}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Button variant="secondary" size="sm" onClick={() => openRecipientRate(r)}>
                        {rate ? <Edit2 size={14} /> : <Plus size={14} />}
                        {rate ? ' Edit' : ' Set rate'}
                      </Button>
                      {rate && (
                        <Button variant="ghost" size="sm" onClick={() => removeRecipientRate(r.id)}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {rates?.single_rate && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#718096', padding: '8px 12px', background: '#F7FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
            Default rate: <strong>${rates.single_rate}/hr</strong> — used for shifts without a client-specific rate.
            <button onClick={() => setDefaultRateOpen(true)} style={{ color: '#2C7A7B', marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>
              Edit
            </button>
          </div>
        )}
      </div>

      {/* ── Earnings summary ──────────────────────────── */}
      {hasRates && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1.5rem' }}>
            <StatCard label="This week" value={`$${stats.weekEarnings.toFixed(2)}`} />
            <StatCard
              label="This month"
              value={`$${stats.monthEarnings.toFixed(2)}`}
              sub={delta ? `${delta > 0 ? '+' : ''}${delta}% vs last month` : `${stats.monthHours.toFixed(1)} hours`}
            />
            {stats.hasAllocationData && (
              <StatCard
                label="Agency billed"
                value={`$${stats.monthAllocation.toFixed(2)}`}
                sub={`You received ${stats.monthAllocation > 0 ? ((stats.monthEarnings / stats.monthAllocation) * 100).toFixed(0) : 0}%`}
                color="#8b5000"
              />
            )}
          </div>

          {/* ── Earnings breakdown table ────────────────── */}
          <Card>
            <div style={{ fontSize: '13px', fontWeight: 500, color: '#718096', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Shift earnings breakdown
            </div>
            {shifts.filter(s => s.clock_out && !s.is_active && s.authorship !== "observed").length === 0 ? (
              <div style={{ padding: '1rem 0', color: '#A0AEC0', fontSize: '14px' }}>No completed shifts yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #EDF2F7' }}>
                    {['Date', 'Recipient', 'Duration', 'Rate', 'Earned', ''].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shifts.filter(s => s.clock_out && !s.is_active && s.authorship !== "observed").slice(0, 20).map(s => {
                    const hours = (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000
                    const r = getRate(s)
                    const earned = hours * r.total
                    const recipientName = s.recipient_id ? recipientMap[s.recipient_id]?.name : null
                    const expanded = expandedRows[s.id]

                    return (
                      <tr key={s.id} style={{ borderBottom: '1px solid #EDF2F7' }}>
                        <td style={tdStyle}>{formatDate(s.date)}</td>
                        <td style={tdStyle}>{recipientName || '—'}</td>
                        <td style={tdStyle}>{formatDuration(s.clock_in, s.clock_out)}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            ${r.total.toFixed(2)}/hr
                            {r.differentials.length > 0 && (
                              <button
                                onClick={() => toggleRow(s.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', padding: '2px' }}
                                title="Show rate breakdown"
                              >
                                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>
                            )}
                          </div>
                          {expanded && r.differentials.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px', lineHeight: 1.6 }}>
                              Base: ${r.base.toFixed(2)}
                              {r.differentials.map((d, i) => (
                                <span key={i}> + ${d.amount.toFixed(2)} {d.name.toLowerCase()}</span>
                              ))}
                              {r.allocation && (
                                <div style={{ color: '#8b5000', marginTop: '2px' }}>
                                  Agency allocation: ${r.allocation.toFixed(2)}/hr (you receive {((r.total / r.allocation) * 100).toFixed(0)}%)
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 600, color: '#1A202C' }}>${earned.toFixed(2)}</td>
                        <td style={tdStyle}>
                          {r.allocation && (
                            <span style={{ fontSize: '11px', color: '#8b5000' }}>
                              of ${(hours * r.allocation).toFixed(2)}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      {/* ── Per-recipient rate modal ──────────────────── */}
      <Modal
        open={rateModalOpen}
        onClose={() => setRateModalOpen(false)}
        title={editingRecipient ? `Pay rate for ${editingRecipient.name}` : 'Configure pay rate'}
        width={520}
      >
        <form onSubmit={handleSaveRecipientRate}>
          {editingRecipient?.agency_name && (
            <div style={{ fontSize: '13px', color: '#718096', marginBottom: '1rem' }}>
              Agency: {editingRecipient.agency_name}
              {editingRecipient.program && ` · Program: ${editingRecipient.program.toUpperCase()}`}
            </div>
          )}

          <Input
            label="Base hourly rate ($)"
            id="base-rate"
            type="number"
            step="0.01"
            min="0"
            value={rateForm.base_rate}
            onChange={e => setRateForm({ ...rateForm, base_rate: e.target.value })}
            placeholder="e.g. 12.00"
            required
          />

          {/* Differentials */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '8px' }}>
              Pay differentials
            </label>
            <p style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', lineHeight: 1.5 }}>
              Add any extra pay on top of the base rate — total care, weekend, skilled care, etc.
            </p>

            {rateForm.differentials.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', padding: '8px 10px', background: '#E1F5EE', borderRadius: '6px' }}>
                <span style={{ flex: 1, fontSize: '14px', color: '#005440', fontWeight: 500 }}>{d.name}</span>
                <span style={{ fontSize: '14px', color: '#005440' }}>+${d.amount.toFixed(2)}/hr</span>
                <button type="button" onClick={() => removeDifferential(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', padding: '2px' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <input
                  list="diff-suggestions"
                  placeholder="e.g. Total care"
                  value={newDiffName}
                  onChange={e => setNewDiffName(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px' }}
                />
                <datalist id="diff-suggestions">
                  {COMMON_DIFFERENTIALS.map(d => <option key={d} value={d} />)}
                </datalist>
              </div>
              <div style={{ width: '100px' }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="$/hr"
                  value={newDiffAmount}
                  onChange={e => setNewDiffAmount(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={addDifferential} disabled={!newDiffName.trim() || !newDiffAmount}>
                <Plus size={14} /> Add
              </Button>
            </div>
          </div>

          {/* Calculated total */}
          <div style={{
            padding: '12px 16px',
            background: '#E1F5EE',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#005440' }}>Your total rate</span>
            <span style={{ fontSize: '22px', fontWeight: 600, color: '#2C7A7B' }}>${calcTotal().toFixed(2)}/hr</span>
          </div>

          {/* Agency allocation (optional) */}
          <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: 500, color: '#4A5568' }}>
                Agency allocation rate (optional)
              </label>
              <div title="What Medicaid pays the agency per hour for this client. This helps you see what percentage you actually receive." style={{ cursor: 'help' }}>
                <Info size={14} style={{ color: '#A0AEC0' }} />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', lineHeight: 1.5 }}>
              If you know what Medicaid pays your agency per hour for this client, enter it here. This helps document what percentage of the allocation you actually receive. This data stays private to you.
            </p>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g. 33.00"
              value={rateForm.agency_allocation}
              onChange={e => setRateForm({ ...rateForm, agency_allocation: e.target.value })}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', marginBottom: '4px' }}
            />
            {rateForm.agency_allocation && parseFloat(rateForm.agency_allocation) > 0 && (
              <div style={{ fontSize: '13px', color: '#8b5000', marginTop: '4px' }}>
                You receive {((calcTotal() / parseFloat(rateForm.agency_allocation)) * 100).toFixed(0)}% of the agency allocation.
                The agency retains ${(parseFloat(rateForm.agency_allocation) - calcTotal()).toFixed(2)}/hr.
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
              Notes (optional)
            </label>
            <input
              type="text"
              placeholder="e.g. ICWP total care rate via Serenity"
              value={rateForm.notes}
              onChange={e => setRateForm({ ...rateForm, notes: e.target.value })}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setRateModalOpen(false)}>Cancel</Button>
            <Button type="submit">Save rate</Button>
          </div>
        </form>
      </Modal>

      {/* ── Default rate modal ───────────────────────── */}
      <Modal open={defaultRateOpen} onClose={() => setDefaultRateOpen(false)} title="Default pay rate" width={400}>
        <form onSubmit={handleSaveDefaultRate}>
          <p style={{ fontSize: '14px', color: '#718096', marginBottom: '1rem', lineHeight: 1.6 }}>
            This rate is used for shifts that don't have a client-specific rate configured. If all your clients pay the same, just set this and skip per-client rates.
          </p>
          <Input
            label="Default hourly rate ($)"
            id="default-rate"
            type="number"
            step="0.01"
            min="0"
            value={defaultRate}
            onChange={e => setDefaultRate(e.target.value)}
            placeholder="e.g. 14.50"
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setDefaultRateOpen(false)}>Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}

const thStyle = { textAlign: 'left', padding: '8px 10px', fontSize: '12px', fontWeight: 500, color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.05em' }
const tdStyle = { padding: '10px', fontSize: '14px', color: '#4A5568' }
